"use client";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { FormField } from "@/schemas/type";
import useSWR from "swr";
import { useFormContext, useWatch } from "react-hook-form";
import { useEffect } from "react";
import { useLang, useSite } from "@/hooks/useSiteInfo";
import { fetchCollectionsData } from "@/lib/fetchData";

interface FormSelectProps {
  field?: FormField;
  placeholder?: string;
  required?: boolean;
  className?: string;
  disabled?: boolean;
  // eslint-disable-next-line no-unused-vars
  onLabelChange?: (label: string, id?: number) => void;
  isFetching?: boolean;
}

export default function FormApplication(props: FormSelectProps) {
  const site = useSite();
  const lang = useLang();

  const { control } = useFormContext<any>();

  const value = useWatch({
    control,
    name: props.field?.name || "",
  });

  const { data, isLoading } = useSWR(
    props.isFetching ? "applicationList" : null,
    () =>
      fetchCollectionsData<{
        id: number;
        slug: string;
        name: string;
      }>("application_category", {
        site: site.toString(),
        lang: lang.toString(),
        fields: ["id", "slug", "name"],
        page: 1,
        limit: 100,
        querys: [
          {
            key: "level",
            value: "1",
          },
        ],
      })
  );

  useEffect(() => {
    if (!isLoading && props.field?.name) {
      const label = data?.data?.find((item) => item.slug === value);
      if (label) {
        props?.onLabelChange?.(label?.name || "");
      } else {
        props.field?.onChange?.(undefined);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isLoading, value]);

  return (
    <Select
      value={props.field?.value}
      onValueChange={(value) => {
        if (isLoading) return;
        if (value) {
          props.field?.onChange?.(value);
        }
      }}
      disabled={props?.disabled}>
      <SelectTrigger
        className={cn(
          "h-10 w-full rounded border-x-0 border-b border-t-0 border-[#F0F0F0] p-0 text-sm hover:border-[#FF5100] focus:border-[#FF5100] data-[disabled]:bg-[#F7F7F7] data-[placeholder]:text-[#A6A6A6] md:border md:border-[#C2C6CE] md:px-4",
          props.className
        )}>
        <SelectValue
          placeholder={
            <span
              className={cn(
                "after:pl-0.5 after:text-[#FF5100]",
                props.required ? "after:content-['*']" : ""
              )}>
              {props.placeholder}
            </span>
          }
        />
      </SelectTrigger>
      <SelectContent className="border-[#C2C6CE] bg-white">
        {data?.data?.map((option) => (
          <SelectItem key={option.id} value={option.slug}>
            {option.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
